import { useEffect, useState } from 'react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import {
  clearJustJoinedOrg,
  readJustJoinedOrg,
} from '~publish/components/InvitationModal'

interface UseJustJoinedCoachmarkResult {
  /** Whether the just-joined coach mark should be shown. */
  isOpen: boolean
  /** Dismiss the coach mark and clear the just-joined flag. */
  dismiss: () => void
}

/**
 * Drives the org-switcher coach mark shown right after an existing user accepts
 * an invitation. `acceptInvitation` stashes the joined org id in `localStorage`
 * before the hard reload (see {@link markJustJoinedOrg}); this reads it back
 * once the app re-bootstraps and only opens the coach mark when the reloaded
 * session actually landed in that org. Dismissing clears the flag so the nudge
 * shows exactly once.
 */
export const useJustJoinedCoachmark = (): UseJustJoinedCoachmarkResult => {
  const currentOrganization = useCurrentOrganization()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!currentOrganization) {
      return
    }
    const justJoinedOrgId = readJustJoinedOrg()
    if (justJoinedOrgId && justJoinedOrgId === currentOrganization.id) {
      setIsOpen(true)
    } else if (justJoinedOrgId) {
      // The flag points at a different org than the one we landed in (e.g. the
      // user switched orgs before the reload settled). Clear the stale flag so
      // it can't fire later against the wrong org.
      clearJustJoinedOrg()
    }
  }, [currentOrganization])

  const dismiss = (): void => {
    clearJustJoinedOrg()
    setIsOpen(false)
  }

  return { isOpen, dismiss }
}
